import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/ui/PageHeader';
import { fetchHealth, fetchOpenApiSpec } from '../api/system';
import { useDelivererSocket } from '../hooks/useDelivererSocket';

export const ObservabilityPage = () => {
  const healthQuery = useQuery({
    queryKey: ['system', 'health'],
    queryFn: fetchHealth,
    refetchInterval: 15_000
  });

  const openApiQuery = useQuery({
    queryKey: ['system', 'openapi'],
    queryFn: fetchOpenApiSpec
  });

  const { connected, error } = useDelivererSocket();

  const openApiSummary = useMemo(() => {
    if (!openApiQuery.data || typeof openApiQuery.data !== 'object') return null;
    const doc = openApiQuery.data as any;
    const title = doc?.info?.title;
    const version = doc?.info?.version;
    const paths = doc?.paths ? Object.keys(doc.paths).length : null;
    return { title, version, paths };
  }, [openApiQuery.data]);

  return (
    <div className="space-y-6">
      <PageHeader title="Observabilité" description="État API, OpenAPI, et WebSocket." />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card p-5 space-y-2">
          <div className="text-sm font-semibold text-slate-900">Health</div>
          {healthQuery.isLoading && <div className="text-sm text-slate-500">Chargement…</div>}
          {healthQuery.isError && <div className="text-sm text-red-600">{(healthQuery.error as Error).message}</div>}
          {healthQuery.isSuccess && (
            <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-auto ring-1 ring-slate-200/60">
              {JSON.stringify(healthQuery.data, null, 2)}
            </pre>
          )}
          <div className="text-xs text-slate-500">Endpoint: `/health`</div>
        </div>

        <div className="card p-5 space-y-2 md:col-span-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">OpenAPI</div>
            <a className="btn-ghost !py-2" href="/api/v1/docs" target="_blank" rel="noreferrer">
              Ouvrir Swagger UI
            </a>
          </div>
          {openApiQuery.isLoading && <div className="text-sm text-slate-500">Chargement…</div>}
          {openApiQuery.isError && <div className="text-sm text-red-600">{(openApiQuery.error as Error).message}</div>}
          {openApiQuery.isSuccess && (
            <div className="text-sm text-slate-700">
              <div>
                <span className="font-medium">Titre:</span> {openApiSummary?.title ?? '—'}
              </div>
              <div>
                <span className="font-medium">Version:</span> {openApiSummary?.version ?? '—'}
              </div>
              <div>
                <span className="font-medium">Nombre de paths:</span> {openApiSummary?.paths ?? '—'}
              </div>
              <div className="mt-3">
                <details className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200/60">
                  <summary className="cursor-pointer text-xs font-medium text-slate-700">Voir le JSON</summary>
                  <pre className="mt-2 text-xs overflow-auto">{JSON.stringify(openApiQuery.data, null, 2)}</pre>
                </details>
              </div>
            </div>
          )}
          <div className="text-xs text-slate-500">Endpoint: `/api/v1/openapi.json`</div>
        </div>
      </div>

      <div className="card p-5 space-y-2">
        <div className="text-sm font-semibold text-slate-900">WebSocket</div>
        <div className="text-sm text-slate-700">
          État: {connected ? <span className="text-emerald-700">connecté</span> : <span>déconnecté</span>}
          {error && <span className="ml-2 text-red-600">({error})</span>}
        </div>
        <div className="text-xs text-slate-500">Connexion: Socket.IO sur `/` (transport websocket)</div>
      </div>
    </div>
  );
};

